/* eslint-disable react/jsx-curly-newline */
/* eslint-disable max-len */
import {
  Alert,
  Backdrop,
  Box,
  CircularProgress,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { TabContext, TabPanel } from '@mui/lab';
import { CustomTab } from 'components/common/custom-tab';
import { ContainedButton } from 'components/ContainedButton';
import React from 'react';
import { useTranslation } from 'react-i18next';
import useAlert from 'utils/alert';

import CreateFileModal from './components/create-file-modal';
import { DocumentTableRow } from './components/rows/documentTableRow';
import { SignDocuments } from './components/sign-documents/sign-documents';
import { DocumentViewer } from './components/table-documents/document-viewer';
import useDocuments from './documents-hooks';
import { downloadFile } from './services/download';
import useStyles from './styles';
import { AutoRow } from './components/rows/AutoRow/index';
import { DeleteConfirmationModal } from 'components/delete-confirmation';
import { EditBundleContentModal } from './components/editBundleContentModal';
import { UploadRow } from './components/rows/UploadRow';
import { EditExpectedFromModal } from './components/editExpectedFromModal';
import { ManualDocumentUploadModal } from './components/manual-document-update-modal/manual-document-update-modal';
import { TemplateRow } from './components/rows/TemplateRow';
import { EditTemplateDataModal } from './components/editTemplateDataModal';
import { AlertUploadFile } from './components/AlertUploadFile';
import {
  documentStepts,
  getDeleteConfirmationMessage,
  getToogleConfirmationMessage,
} from './utils';
import { LoadingSkeleton } from './components/LoadingSkeleton';
import { RequestFileUploadModal } from './components/request-file-upload-modal';
import { Filters } from './components/Filters';
import { useDriver } from 'hooks/useDriver';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { RequestFileSignatureModal } from './components/request-file-signature-modal';

export const Documents = () => {
  const { mainContainer, title, container, backdrop, headerCell } = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { t } = useTranslation();
  const {
    isDocumentsLoading,
    modalActions: {
      closeModal,
      openAlertFileUploadProcessModal,
      openCreateDocumentModal,
      openDeleteDocumentModal,
      openDeleteFileModal,
      openDocumentModal,
      openEditPdfZipBundleModal,
      openLockToggleModal,
      openViewModal,
      openRequestFileUploadModal,
      openFileSignatureRequestModal
    },
    modalState,
    kindTabs,
    currentKindTabSelected,
    documents,
    orderId,
    estates,
    parties,
    handleCreateFile,
    templates,
    openBackdrop,
    allFilesinDocuments,
    onSearch,
    htmlDocumentsContainerRef,
    searchWordClass,
    showAlerUpdate,
    onSelectKindTab,
    selectedTabDocuments,
    tableHeaders,
    toggleLockDocument,
    isUpdateFileLoading,
    updatePdfZipParts,
    deleteDocument,
    isDeleteDocumentLoading,
    updateTemplateData,
    isUpdateTemplateFileDataLoading,
    queryInputRef,
    signTabQuery
  } = useDocuments();

  const { initTutorial } = useDriver({
    stepts: documentStepts,
  });

  const showAlert = useAlert();

  const handleTabChange = (_: any, value: string) => {
    onSelectKindTab(value);
  };

  const handleDownload = async (link: string, fileName: string) => {
    const success = await downloadFile(link, fileName);
    if (success) {
      showAlert(t('documents:document-download-success'), 'success');
    } else {
      showAlert(t('documents:error-downloading'), 'error');
    }
  };

  return (
    <Box className={container}>
      <Box style={{ marginBottom: '1rem' }} id="documents-view">
        <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: theme.spacing(1), paddingLeft: isMobile ? theme.spacing(2) : 0, paddingRight: isMobile ? theme.spacing(2) : 0 }}>
          <Box style={{ display: 'flex', alignItems: 'center' }}>
            <Typography className={title} variant="h1">
              {t('orders:documents')}
            </Typography>
            <Tooltip title={t('documents:tutorial')}>
              <IconButton size="small" onClick={initTutorial} style={{ padding: isMobile ? theme.spacing(1.5) : undefined }}>
                <FontAwesomeIcon icon="question-circle" size="sm" />
              </IconButton>
            </Tooltip>
          </Box>
          <Box style={{ display: 'flex', gap: theme.spacing(1) }}>
            <ContainedButton
              text={!isMobile ? t('documents:add-document') : undefined}
              onClick={() => openCreateDocumentModal()}
              size={isMobile ? 'small' : 'medium'}
              icon="plus"
              elementId="add-document"
            />
            {currentKindTabSelected === 'upload' && (
            <ContainedButton
              text={t('documents:request-file')}
              onClick={() => openRequestFileUploadModal()}
              size="medium"
            />
            )}
          </Box>
        </Box>
      </Box>
      <Paper className={mainContainer}>
        {showAlerUpdate && (
          <Box display="flex" flex="1">
            <Alert severity="info">
              <Typography style={{ fontWeight: 500, color: 'black' }} variant="body2">
                {t('documents:documents-updating-message')}
              </Typography>
            </Alert>
          </Box>
        )}
        <Box id="documents-container-list">
          <Filters parties={parties} estates={estates} onSearch={onSearch} queryInputRef={queryInputRef} />

          {isDocumentsLoading && documents.length === 0 && <LoadingSkeleton />}
          <TabContext value={currentKindTabSelected}>
            <Tabs
              orientation="horizontal"
              variant="scrollable"
              scrollButtons
              value={currentKindTabSelected}
              onChange={handleTabChange}
              style={{ borderBottom: '1px solid lightgray' }}
              id="kind-tabs"
              allowScrollButtonsMobile
            >
              {kindTabs.map((tab) => (
                <CustomTab
                  key={tab.kind}
                  value={tab.kind}
                  currentValue={currentKindTabSelected}
                  text={tab.label}
                />
              ))}
            </Tabs>
            <Box>
              {['accounting', 'package', 'zip', 'pdf'].includes(currentKindTabSelected) && (
                <TableContainer style={{ padding: 0, marginTop: '1rem' }}>
                  {selectedTabDocuments.length > 0 && (
                    <Table style={{ width: '100%', tableLayout: isMobile ? 'fixed' : 'auto' }}>
                      {!isMobile && (
                        <TableHead>
                          <TableRow style={{ height: '68px' }}>
                            {tableHeaders.map((cell) => (
                              <TableCell className={headerCell} key={cell.id}>
                                {cell.label}
                              </TableCell>
                            ))}
                          </TableRow>
                        </TableHead>
                      )}
                      <TableBody>
                        {selectedTabDocuments.map((item) => (
                          <DocumentTableRow
                            kind={currentKindTabSelected}
                            document={item}
                            handleDownload={handleDownload}
                            viewFile={openViewModal}
                            editDocumentBundle={() => openEditPdfZipBundleModal(item)}
                            deleteDocument={() => openDeleteDocumentModal(item)}
                          />
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </TableContainer>
              )}
              <TabPanel value="auto" style={{ padding: 0, marginTop: '1rem' }}>
                <Box style={{ display: 'flex', gap: '1rem', flexDirection: 'column' }}>
                  {selectedTabDocuments.map(({ description, archives, locked, _id }) => (
                    <AutoRow
                      key={_id}
                      documentId={_id}
                      locked={locked}
                      description={description}
                      archives={archives}
                      handleDownload={handleDownload}
                      viewFile={openViewModal}
                      onLockedToggle={openLockToggleModal}
                      onRequestSignature={(fileSource) => openFileSignatureRequestModal("requestSignature", _id, fileSource, description)}
                    />
                  ))}
                </Box>
                {modalState.type === 'toggleDocumentLock' && (
                  <DeleteConfirmationModal
                    isOpen
                    onClose={closeModal}
                    confirmationMessage={getToogleConfirmationMessage(modalState)}
                    isRemoveLoading={isUpdateFileLoading}
                    buttonConfirmText={modalState.lock ? t('documents:lock') : t('documents:unlock')}
                    title={modalState.lock ? t('documents:lock-document') : t('documents:unlock-document')}
                    onConfirm={toggleLockDocument}
                  />
                )}
              </TabPanel>

              <TabPanel value="upload" style={{ padding: 0, marginTop: '1rem' }}>
                <Box style={{ display: 'flex', gap: '1rem', flexDirection: 'column' }}>
                  {selectedTabDocuments.map((document) => (
                    <UploadRow
                      key={document._id}
                      document={document}
                      handleDownload={handleDownload}
                      viewFile={openViewModal}
                      onEditExpectedFrom={() => openDocumentModal(document, 'editExpectedFrom')}
                      onUploadDocumentFiles={() =>
                        openDocumentModal(document, 'uploadDocumentFiles')
                       }
                      onDeleteDocument={() => openDeleteDocumentModal(document)}
                      onDeleteFile={(file) => openDeleteFileModal(file)}
                    />
                  ))}
                </Box>
                {modalState.type === 'editExpectedFrom' && (
                  <EditExpectedFromModal
                    isOpen
                    isSavingLoading={isUpdateTemplateFileDataLoading}
                    onClose={closeModal}
                    onSave={updateTemplateData}
                    parties={parties}
                    currentExpectedByPartiesList={
                      modalState.document?.data
                        .filter(({ kind }) => kind === 'party')
                        .map(({ _id, name, kinds }) => ({
                          _id,
                          name: name!,
                          kinds: kinds ?? [],
                        })) ?? []
                    }
                  />
                )}

                {modalState.type === 'requestFileUploadModal' && (
                  <RequestFileUploadModal
                    open
                    documents={documents}
                    handleClose={closeModal}
                    parties={parties}
                  />
                )}
              </TabPanel>

              <TabPanel value="template" style={{ padding: 0, marginTop: '1rem' }}>
                <Box style={{ display: 'flex', gap: '1rem', flexDirection: 'column' }}>
                  {selectedTabDocuments.map((document) => (
                    <TemplateRow
                      key={document._id}
                      document={document}
                      handleDownload={handleDownload}
                      viewFile={openViewModal}
                      onDeleteDocument={() => openDeleteDocumentModal(document)}
                      editTemplate={() => openDocumentModal(document, 'editTemplateData')}
                      onRequestSignature={(fileSource) => openFileSignatureRequestModal("requestSignature", document._id, fileSource, document.description)}
                    />
                  ))}
                </Box>
                {modalState.type === 'editTemplateData' && (
                  <EditTemplateDataModal
                    isSavingLoading={isUpdateTemplateFileDataLoading}
                    onClose={closeModal}
                    onSave={updateTemplateData}
                    documentData={modalState.document.data ?? []}
                    parties={parties}
                    properties={estates}
                  />
                )}
              </TabPanel>

              <TabPanel value="sign" style={{ padding: 0 }}>
                <SignDocuments query={signTabQuery} />
              </TabPanel>
            </Box>
          </TabContext>
        </Box>
      </Paper>

      <AlertUploadFile open={modalState.type === 'alertFileUploadProcess'} onClose={closeModal} />

      {modalState.type === 'editBundleZipPdfContent' && (
        <EditBundleContentModal
          kind={currentKindTabSelected}
          isSavingLoading={isUpdateFileLoading}
          onClose={closeModal}
          isOpen
          parties={parties}
          estates={estates}
          includedDocuments={modalState.document?.parts?.map((part) => part.source) ?? []}
          allFilesinDocuments={allFilesinDocuments}
          onSave={(bundle) => updatePdfZipParts(bundle)}
        />
      )}

      {modalState.type === 'uploadDocumentFiles' && (
        <ManualDocumentUploadModal
          open
          document={modalState.document!}
          handleClose={closeModal}
          onUpdateScuccess={openAlertFileUploadProcessModal}
        />
      )}

      {modalState.type === "requestSignature" && (
        <RequestFileSignatureModal
          open
          documentId={modalState.documentId}
          fileSource={modalState.fileSource}
          documentName={modalState.documentName}
          handleClose={closeModal}
        />
      )}

      <DeleteConfirmationModal
        isOpen={['deleteDocumentConfirmation', 'deleteFileConfirmation'].includes(modalState.type)}
        onClose={closeModal}
        confirmationMessage={getDeleteConfirmationMessage(modalState)}
        isRemoveLoading={isDeleteDocumentLoading}
        buttonConfirmText={t('common:delete')}
        title={modalState.type === 'deleteDocumentConfirmation' ? t('documents:delete-document') : t('documents:delete-file')}
        onConfirm={deleteDocument}
      />

      <DocumentViewer
        open={modalState.type === 'view'}
        handleClose={closeModal}
        orderId={orderId}
        archive_id={modalState.type === 'view' ? modalState.fileId : ''}
      />

      <CreateFileModal
        selectedKindTab={currentKindTabSelected}
        open={modalState.type === 'createNewDocument'}
        getTemplates={templates}
        handleClose={closeModal}
        handleCreateFile={handleCreateFile}
      />

      <Backdrop open={openBackdrop} className={backdrop}>
        <CircularProgress color="inherit" />
      </Backdrop>
    </Box>
  );
};
